/**
 * CustomComponentsPane (ADR-0046) — the per-app "Libreria componenti" manager,
 * opened from the builder rail's BOTTOM (app-global) zone as a main-pane context.
 *
 * A custom component is reusable author code referenced by `custom_component`
 * page nodes (`source="library"`). Its authoring TYPE is chosen ONCE at creation
 * (a modal) and then fixed (shown as a badge): **HTML/CSS/JS** or **React** — the
 * two-mode runtime (ADR-0046, slice 3b). Both render through one sandboxed-iframe
 * host + the `tray.*` bridge.
 *
 * Master/detail: a list on the left, a FULL-HEIGHT editor on the right. HTML/CSS/JS
 * components get THREE editor tabs (HTML · CSS · JS) over the stored `{html,css,js}`
 * blob; React gets a single editor.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, Code2, FileCode2, Plus, Trash2 } from "lucide-react";

import { CodeEditor, type EditorLanguage } from "@/components/canonical/CodeEditor";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApiError } from "@/lib/api";
import {
  appsApi,
  type CustomComponentMode,
  type CustomComponentOut,
} from "@/lib/api-apps";
import {
  codeFromHtmlParts,
  htmlPartsFromCode,
  type HtmlParts,
} from "@/lib/customComponent";
import { libraryGlobalsDts } from "@/lib/pageLibraryTypes";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

const REACT_STARTER = `function Component({ model }) {
  return <div>{JSON.stringify(model.vars)}</div>;
}`;

const HTML_STARTER = codeFromHtmlParts({
  html: '<div class="card">\n  <h3>Componente</h3>\n  <p data-role="out"></p>\n</div>',
  css: ".card { padding: 12px; border: 1px solid var(--cc-border, #e2e8f0); border-radius: 8px; }",
  js: `// In scope: root (il tuo markup), tray (model + azioni), require, props.
function render(m) {
  root.querySelector('[data-role="out"]').textContent = JSON.stringify(m.vars);
}
render(tray.model);
tray.onModel(render); // ri-renderizza quando lo stato della pagina cambia`,
});

/** Ambient TS so `tray.` / `root.` autocomplete in the HTML-JS + React editors. */
const TRAY_DTS = `
declare const React: any;
interface TrayQueryResult { rows: any[]; total: number; data: any; }
interface TrayModel {
  vars: Record<string, any>;
  queries: Record<string, TrayQueryResult>;
  user: { id: string; email: string; full_name?: string | null } | null;
  role: { id: string; slug: string; name: string } | null;
  appSlug: string; pageSlug: string;
}
interface Tray extends TrayModel {
  /** Live model snapshot (read). */
  model: TrayModel;
  setVar(name: string, value: any): void;
  refresh(): void;
  navigate(to: string): void;
  toast(message: string): void;
  /** Subscribe to page-state changes; returns an unsubscribe fn. */
  onModel(cb: (m: TrayModel) => void): () => void;
}
/** HTML/CSS/JS mode globals. */
declare const root: HTMLElement;
declare const tray: Tray;
declare const props: Record<string, any>;
declare function require(name: string): any;
/** React mode: the model prop + hooks (exposed as frame globals). */
declare const model: TrayModel & { setVar(n: string, v: any): void; refresh(): void; navigate(to: string): void; toast(m: string): void };
declare function useTrayModel(): typeof model;
declare function useState<T = any>(initial: T | (() => T)): [T, (v: T | ((p: T) => T)) => void];
declare function useEffect(fn: () => void | (() => void), deps?: any[]): void;
declare function useMemo<T = any>(fn: () => T, deps?: any[]): T;
declare function useRef<T = any>(initial: T): { current: T };
`;

const HTML_TABS: { key: keyof HtmlParts; label: string; lang: EditorLanguage }[] = [
  { key: "html", label: "HTML", lang: "html" },
  { key: "css", label: "CSS", lang: "css" },
  { key: "js", label: "JS", lang: "javascript" },
];

type AreaView = "list" | "detail" | "full";

export function CustomComponentsPane({
  appId,
  canWrite = true,
  view = "full",
  selectedId: selectedIdProp,
  onSelectId,
}: {
  appId: string;
  canWrite?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split). Omit for the legacy single pane. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const componentsQ = useQuery({
    queryKey: ["apps", appId, "custom-components"],
    queryFn: () => appsApi.listCustomComponents(appId),
  });
  const components = useMemo(() => componentsQ.data ?? [], [componentsQ.data]);
  // ADR-0046 Slice 4 — the app's libraries autocomplete inside the component
  // editor too (they're injected into the frame via `require()` / the global).
  const librariesQ = useQuery({
    queryKey: ["apps", appId, "js-libraries"],
    queryFn: () => appsApi.listJsLibraries(appId),
  });
  const ambientDts = useMemo(
    () => TRAY_DTS + libraryGlobalsDts(librariesQ.data),
    [librariesQ.data],
  );

  const controlled = onSelectId !== undefined;
  const [internalId, setInternalId] = useState<string | null>(null);
  const selectedId = controlled ? (selectedIdProp ?? null) : internalId;
  const setSelectedId = (id: string | null) => (controlled ? onSelectId!(id) : setInternalId(id));
  const [draft, setDraft] = useState<CustomComponentOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [htmlTab, setHtmlTab] = useState<keyof HtmlParts>("html");

  const selected = components.find((c) => c.id === selectedId) ?? null;
  useEffect(() => {
    if (selected && (!draft || draft.id !== selected.id)) {
      setDraft(selected);
      setHtmlTab("html");
    }
    if (!selected) setDraft(null);
  }, [selected, draft]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["apps", appId, "custom-components"] });

  const createMut = useMutation({
    mutationFn: (mode: CustomComponentMode) => {
      const taken = new Set(components.map((c) => c.name));
      let name = "Componente";
      let i = 1;
      while (taken.has(name)) name = `Componente${++i}`;
      return appsApi.createCustomComponent(appId, {
        name,
        code: mode === "html" ? HTML_STARTER : REACT_STARTER,
        mode,
      });
    },
    onSuccess: (cc) => {
      invalidate();
      setSelectedId(cc.id);
      setDraft(cc);
      setError(null);
      setCreateOpen(false);
      setHtmlTab("html");
    },
    onError: (e) => setError(errMsg(e)),
  });
  const saveMut = useMutation({
    mutationFn: (d: CustomComponentOut) =>
      appsApi.updateCustomComponent(appId, d.id, {
        name: d.name,
        code: d.code,
        description: d.description,
        mode: d.mode,
      }),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(errMsg(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => appsApi.deleteCustomComponent(appId, id),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
      setDraft(null);
    },
    onError: (e) => setError(errMsg(e)),
  });

  // For an HTML component the editor edits the three parts of the JSON `code`.
  const parts = useMemo<HtmlParts>(
    () => (draft && draft.mode === "html" ? htmlPartsFromCode(draft.code) : { html: "", css: "", js: "" }),
    [draft],
  );
  const setPart = (key: keyof HtmlParts, value: string) => {
    if (!draft) return;
    setDraft({ ...draft, code: codeFromHtmlParts({ ...parts, [key]: value }) });
  };

  const masterCls =
    view === "full"
      ? "flex w-52 shrink-0 flex-col gap-1 border-r border-border/60 pr-2"
      : "flex h-full flex-col gap-1 overflow-y-auto text-[12px]";

  const master = (
    <div className={masterCls}>
        <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Componenti
        </div>
        {canWrite && (
          <button
            type="button"
            className="mb-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
            onClick={() => setCreateOpen(true)}
            disabled={createMut.isPending}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuovo componente
          </button>
        )}
        {components.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">Nessun componente.</p>
        ) : (
          components.map((c) => {
            const active = c.id === selectedId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedId(c.id)}
                className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[12px] ${
                  active ? "bg-accent/15 text-accent" : "hover:bg-surface"
                }`}
              >
                <Boxes className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                <span className="shrink-0 text-[9px] uppercase text-muted-foreground/70">
                  {c.mode === "html" ? "html" : "react"}
                </span>
              </button>
            );
          })
        )}
      </div>
  );

  const detailInner = !draft ? (
          <div className="flex flex-1 items-center justify-center text-[11px] text-muted-foreground">
            Seleziona un componente o creane uno nuovo.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="Nome componente"
                className="w-48 rounded border border-border bg-background px-1.5 py-1 font-mono"
                value={draft.name}
                disabled={!canWrite}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
              {/* The TYPE is fixed at creation — shown as a badge, not a toggle. */}
              <span className="inline-flex items-center gap-1 rounded border border-border bg-surface px-1.5 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {draft.mode === "html" ? (
                  <>
                    <FileCode2 className="h-3 w-3" /> HTML/CSS/JS
                  </>
                ) : (
                  <>
                    <Code2 className="h-3 w-3" /> React
                  </>
                )}
              </span>
              <div className="flex-1" />
              {canWrite && (
                <>
                  <button
                    type="button"
                    className="rounded bg-accent px-2.5 py-1 text-[11px] font-medium text-accent-foreground disabled:opacity-50"
                    onClick={() => saveMut.mutate(draft)}
                    disabled={saveMut.isPending}
                  >
                    Salva
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-danger disabled:opacity-50"
                    onClick={() => {
                      if (window.confirm(`Eliminare il componente "${draft.name}"?`))
                        deleteMut.mutate(draft.id);
                    }}
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Elimina
                  </button>
                </>
              )}
            </div>

            <input
              aria-label="Descrizione"
              className="w-full rounded border border-border bg-background px-1.5 py-1 text-[11px]"
              placeholder="Descrizione (opzionale)"
              value={draft.description ?? ""}
              disabled={!canWrite}
              onChange={(e) => setDraft({ ...draft, description: e.target.value || null })}
            />

            {error && <p className="text-[11px] text-danger">{error}</p>}

            {/* Full-height editor (line-number gutter on — NOT inline). */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-border">
              {draft.mode === "html" ? (
                <>
                  {/* HTML · CSS · JS tabs over the {html,css,js} blob. */}
                  <div className="flex shrink-0 items-center border-b border-border bg-surface/60 px-1.5 py-1 text-[11px]">
                    {HTML_TABS.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        aria-pressed={htmlTab === t.key}
                        className={`rounded px-2 py-0.5 font-medium ${
                          htmlTab === t.key
                            ? "bg-accent text-accent-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                        onClick={() => setHtmlTab(t.key)}
                      >
                        {t.label}
                      </button>
                    ))}
                    <span className="ml-auto pr-1 text-[10px] text-muted-foreground/70">
                      root · tray · require · props
                    </span>
                  </div>
                  <div className="min-h-0 flex-1">
                    {HTML_TABS.map((t) => (
                      <div key={t.key} className={`h-full ${htmlTab === t.key ? "" : "hidden"}`}>
                        <CodeEditor
                          language={t.lang}
                          value={parts[t.key]}
                          onChange={(v) => setPart(t.key, v)}
                          height="100%"
                          readOnly={!canWrite}
                          jsAmbientTypes={t.lang === "javascript" ? ambientDts : undefined}
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-full">
                  <CodeEditor
                    language="javascript"
                    value={draft.code}
                    onChange={(code) => setDraft({ ...draft, code })}
                    height="100%"
                    readOnly={!canWrite}
                    jsAmbientTypes={ambientDts}
                  />
                </div>
              )}
            </div>
          </>
        );

  const createDialog = (
    <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Nuovo componente</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="mb-3 text-[12px] text-muted-foreground">
              Scegli il tipo di componente. Non è modificabile dopo la creazione.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={createMut.isPending}
                className="flex flex-col items-start gap-1.5 rounded-md border border-border p-3 text-left hover:border-accent/60 hover:bg-surface disabled:opacity-50"
                onClick={() => createMut.mutate("html")}
              >
                <FileCode2 className="h-5 w-5 text-accent" />
                <span className="text-[13px] font-medium">HTML/CSS/JS</span>
                <span className="text-[11px] text-muted-foreground">
                  Markup + stile + comportamento, con il bridge <code className="font-mono">tray</code>.
                </span>
              </button>
              <button
                type="button"
                disabled={createMut.isPending}
                className="flex flex-col items-start gap-1.5 rounded-md border border-border p-3 text-left hover:border-accent/60 hover:bg-surface disabled:opacity-50"
                onClick={() => createMut.mutate("react")}
              >
                <Code2 className="h-5 w-5 text-accent" />
                <span className="text-[13px] font-medium">React</span>
                <span className="text-[11px] text-muted-foreground">
                  Un componente React (JSX) con <code className="font-mono">function Component</code> e i hook.
                </span>
              </button>
            </div>
            {error && <p className="mt-2 text-[11px] text-danger">{error}</p>}
          </DialogBody>
        </DialogContent>
      </Dialog>
  );

  if (view === "list")
    return (
      <>
        {master}
        {createDialog}
      </>
    );
  if (view === "detail")
    return (
      <div className="flex h-full min-h-0 flex-col gap-2 text-[12px]">
        {detailInner}
        {createDialog}
      </div>
    );

  return (
    <div className="flex h-full gap-3 text-[12px]">
      {master}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">{detailInner}</div>
      {createDialog}
    </div>
  );
}
